import axios, {CancelToken} from 'axios';
import qs from 'query-string';

import {get} from '-/util/lodash';
import {getOfferId} from '-/util/util';

const prefix = '/api/refi';

const sessionPoll = async() => {
    const res = await axios.post(`${prefix}/sessionPoll`);
    const {isValid} = res.data;

    if (!isValid) {
        window.location.reload();
    }

    return isValid;
};

const imgUrl = (imgType, offerId, creditorId = '') => {
    return `${prefix}/img/${imgType}/${offerId}/?creditorId=${creditorId}`;
};

const stylesheetUrl = (offerId, creditorId = '') => {
    return `${prefix}/stylesheet/${offerId}/?creditorId=${creditorId}`;
};

const pdfUrl = (opts, type) => {
    const {offerId, creditorId, isCCRDomain} = opts; 
    let url = `${prefix}/${type}`;

    if (offerId) {
        url += `/${offerId}`;
    }

    if (creditorId && isCCRDomain) {
        url += `?creditorId=${creditorId}`; 
    }

    return url;
};

const retrieveDocumentLink = (offerId, documentId) => {
    return axios.get(`${prefix}/retrieveDocumentLink/${offerId}/${documentId}`);
};

const getPartialInfo = (creditorId = '') => {
    return axios.get(`${prefix}/partialInfo/?offerId=${getOfferId()}&creditorId=${creditorId}`);
};

const getPartialInfoForMultipleOffers = offerIds => {
    return axios.get(`${prefix}/partialInfoForMultipleOffers?offerIds=${offerIds.join(',')}`);
};

const getInfo = (params = {}) => {
    const {offerId, pin} = params;
    const query = {
        ...offerId && {offerId},
        ...pin && {pin}
    };
    const queryString = qs.parse(location.search);

    return axios.get(`${prefix}/info`, {params: {
        ...query,
        ...queryString,
        url: window.location.href,
        referrer: document.referrer
    }});
};

const getOfferByPIN = (pin, creditor) => {
    return axios.get(`${prefix}/getOfferByPIN/${pin}/${creditor}`);
};

const manualEntry = formInfo => {
    return axios.post(`${prefix}/manualEntry`, formInfo);
};

const createOffer = formInfo => {
    const searchParams = qs.parse(location.search);
    const {r: referralCode} = searchParams;

    if (referralCode) {
        formInfo.referralCode = referralCode;
    }
    
    return axios.post(`${prefix}/createOffer`, formInfo);
};

const checkReferral = data => {
    return axios.post(`${prefix}/checkReferralValidity`, data);
};

const cancelMsg = 'Operation canceled by the user.';

let authCancelSource;
const auth = o => {
    if (authCancelSource) {
        authCancelSource.cancel(cancelMsg);
    }
    authCancelSource = CancelToken.source();
    return axios.post(`${prefix}/auth`, o, {cancelToken: authCancelSource.token});
};

const saveSSN = obj => {
    return axios.post(`${prefix}/saveSSN`, obj);
};

const timeoutInMs = 5000;

const getExperianDescription = async opts => {
    const {name, licensePlate, licenseState} = opts;
    const baseUrl = `${prefix}/experian/describeVehicleWithPlate`;
    const url = `${baseUrl}/${name}/${licensePlate}/${licenseState}`;
    const {data, error} = await axios.get(url, {timeout: timeoutInMs});

    if (error) {
        throw new Error(`Error getting information from Experian with info ${opts}`, error);
    }

    return data;
};

const lookupVIN = async opts => {
    const {includeExperian, includeNADA, vin} = opts;

    if (!includeExperian && !includeNADA) {
        throw new Error('At least one part must be provided');
    }

    const {error, data} = await axios.post(`${prefix}/lookupVIN`, opts, {timeout: timeoutInMs});

    if (error) {
        throw new Error(`Error looking up VIN for ${vin}`, error);
    }

    return data;
};

const getNADAMakes = async opts => {
    const {year} = opts;
    const {data, error} = await axios.get(`${prefix}/nada/getMakes/${year}`, {timeout: timeoutInMs});

    if (error) {
        throw new Error(`Error getting makes from NADA for year ${year}`, error);
    }

    return data;
};

const getNADAModels = async opts => {
    const {year, make} = opts;
    const {data, error} = await axios.get(`${prefix}/nada/getModels/${year}/${make}`, {timeout: timeoutInMs});

    if (error) {
        throw new Error(`Error getting models from NADA for year ${year} and make ${make}`, error);
    }

    return data;
};

const getNADAStyles = async opts => {
    const {year, make, model} = opts;
    const {data, error} = await axios.get(`${prefix}/nada/getTrims/${year}/${make}/${model}`, {timeout: timeoutInMs});

    if (error) {
        throw new Error(`Error getting styles from NADA for year ${year}, make ${make} and model ${model}`, error);
    }

    return data;
};

let cancelAdditionalVehicle;
const saveAdditionalVehicle = o => {
    if (cancelAdditionalVehicle) {
        cancelAdditionalVehicle.cancel(cancelMsg);
    }
    cancelAdditionalVehicle = CancelToken.source();
    return axios.post(`${prefix}/saveAdditionalVehicle`, o, {cancelToken: cancelAdditionalVehicle.token});
};

let submitCancelSource;
const save = o => {
    if (submitCancelSource) {
        submitCancelSource.cancel(cancelMsg);
    }
    submitCancelSource = CancelToken.source();
    return axios.post(`${prefix}/save`, o, {cancelToken: submitCancelSource.token});
};

let submitFinalCancelSource;
const submitFinal = o => {
    if (submitFinalCancelSource) {
        submitFinalCancelSource.cancel(cancelMsg);
    }
    submitFinalCancelSource = CancelToken.source();
    return axios.post(`${prefix}/submitFinal`, o, {cancelToken: submitFinalCancelSource.token});
};

let validateCCRCancelSource;
const validateCCR = o => {
    if (validateCCRCancelSource) {
        validateCCRCancelSource.cancel(cancelMsg);
    }
    validateCCRCancelSource = CancelToken.source();
    return axios.post(`${prefix}/validateCCR`, o, {cancelToken: validateCCRCancelSource.token});
};

let explicitSaveCancelSource;
const explicitSave = o => {
    if (explicitSaveCancelSource) {
        explicitSaveCancelSource.cancel(cancelMsg);
    }
    explicitSaveCancelSource = CancelToken.source();
    return axios.post(`${prefix}/explicitSave`, o, {cancelToken: explicitSaveCancelSource.token});
};

let requestCallbackSource;
const requestCallback = o => {
    if (requestCallbackSource) {
        requestCallbackSource.cancel(cancelMsg);
    }
    requestCallbackSource = CancelToken.source();
    return axios.post(`${prefix}/requestCallback`, o, {cancelToken: requestCallbackSource.token});
};

const queryString = params => Object.keys(params).map(key => `${key}=${params[key]}`).join('&');

let uploadImageSource;
const uploadImage = ({offerId, stipType, applicantType, indexOfDoc}, formData, options = {}) => {
    if (uploadImageSource) {
        uploadImageSource.cancel(cancelMsg);
    }
    uploadImageSource = CancelToken.source();
    return axios.post(`${prefix}/uploadStip?${queryString({_id: offerId, stipType, applicantType, indexOfDoc})}`, formData, options);
};

let downloadImageSrc;
const downloadImage = ({offerId, stipType, applicantType, indexOfDoc}) => {
    if (downloadImageSrc) {
        downloadImageSrc.cancel(cancelMsg);
    }
    downloadImageSrc = CancelToken.source();
    return axios.get(`${prefix}/stipImg?${queryString({_id: offerId, stipType, applicantType, indexOfDoc})}`);
};

let addDocSrc;
const addDoc = ({offerId, stipType, applicantType}) => {
    if (addDocSrc) {
        addDocSrc.cancel(cancelMsg);
    }
    addDocSrc = CancelToken.source();
    return axios.post(`${prefix}/addDoc?${queryString({_id: offerId, stipType, applicantType})}`);
};

let removeDocSrc;
const removeDoc = params => {
    const {offerId, ...rest} = params;

    if (removeDocSrc) {
        removeDocSrc.cancel(cancelMsg);
    }
    removeDocSrc = CancelToken.source();
    return axios.post(`${prefix}/removeDoc?${queryString({_id: offerId, ...rest})}`);
};

let saveDocSource;
const saveDoc = (queryParams, formData) => {
    if (saveDocSource) {
        saveDocSource.cancel(cancelMsg);
    }
    saveDocSource = CancelToken.source();
    return axios.post(`${prefix}/saveDoc?${queryString(queryParams)}`, formData);
};

const isCancel = err => {
    return axios.isCancel(err);
};

const getContactSVGUrl = () => {
    return `${prefix}/call`;
};

const saveContactByReq = async(data = {}) => {
    const res = await axios.post(`${prefix}/contact`, data);
    return get(res, 'data', {});
};

const getCreditorBySegment = async(segmentId = '') => {
    const res = await axios.post(`${prefix}/getCreditorBySegment`, segmentId);
    return get(res, 'data', {});
};

const getSegmentById = async(segmentId = '') => {
    const res = await axios.post(`${prefix}/getSegmentById`, {segmentId});
    return get(res, 'data', {});
};

const sendErrorToDevs = async errorInfoObj => {
    const res = await axios.post(`${prefix}/sendErrorToDevs`, {errorInfoObj});
    return get(res, 'data', {});
};

const duplicateOffer = async props => {
    const res = await axios.post(`${prefix}/duplicateOffer`, props);
    return get(res, 'data', {});
};

export {
    duplicateOffer,
    getExperianDescription,
    getOfferByPIN,
    getInfo,
    getPartialInfo,
    getPartialInfoForMultipleOffers,
    auth,
    lookupVIN,
    save,
    uploadImage,
    downloadImage,
    isCancel,
    getContactSVGUrl,
    retrieveDocumentLink,
    requestCallback,
    submitFinal,
    explicitSave,
    validateCCR,
    imgUrl,
    stylesheetUrl,
    addDoc,
    removeDoc,
    saveDoc,
    saveSSN,
    manualEntry,
    createOffer,
    pdfUrl,
    getNADAMakes,
    getNADAModels,
    getNADAStyles,
    getCreditorBySegment,
    saveContactByReq,
    getSegmentById,
    saveAdditionalVehicle,
    sendErrorToDevs,
    sessionPoll,
    checkReferral
};
